import type {
  WorkspaceOperationOutcome,
  WorkspaceOperationRequest,
} from '@streamlain/copilot-realtime-contract';

import { failure } from './workspace-operation-outcome';
import type { WorkspaceViewContextService } from './workspace-view-context';

const MAX_RETAINED_REQUESTS = 128;

export class WorkspaceOperationExecutor {
  private disposed = false;
  private running: AbortController | null = null;
  private readonly starts = new Set<() => void | Promise<void>>();
  private readonly requests = new Map<
    string,
    { identity: string; result: Promise<WorkspaceOperationOutcome> }
  >();
  constructor(private readonly context: WorkspaceViewContextService) {}

  /**
   * Tells when this tab is about to operate an element. The operation waits for
   * what is returned, so the view can make room before the element is looked at.
   */
  onStart(start: () => void | Promise<void>): () => void {
    this.starts.add(start);
    return () => this.starts.delete(start);
  }

  /** True when this tab handed out the view the request was made against. */
  issued(request: WorkspaceOperationRequest): boolean {
    return this.context.wasIssued(request, request.revision);
  }

  invoke(
    request: WorkspaceOperationRequest,
    signal?: AbortSignal
  ): Promise<WorkspaceOperationOutcome> {
    const identity = JSON.stringify([
      request.workspaceId,
      request.viewId,
      request.revision.epoch,
      request.revision.sequence,
      request.elementKey,
      request.agent,
      request.verb,
      request.text ?? null,
      request.after ?? null,
      request.replace ?? null,
      request.key ?? null,
      request.at ? [request.at.x, request.at.y] : null,
      request.to ? [request.to.x, request.to.y] : null,
      request.fields?.map(field => [field.elementKey, field.text]) ?? null,
    ]);
    // A replay returns the first outcome; an operation never runs twice.
    const retained = this.requests.get(request.requestId);
    if (retained)
      return retained.identity === identity
        ? retained.result
        : Promise.resolve(failure('blocked'));
    if (this.disposed) return Promise.resolve(failure('stale'));
    if (this.running || !request.requestId.trim())
      return Promise.resolve(failure('blocked'));
    if (this.requests.size >= MAX_RETAINED_REQUESTS)
      this.requests.delete(this.requests.keys().next().value!);
    const controller = new AbortController();
    this.running = controller;
    const result = this.execute(
      {
        ...request,
        revision: { ...request.revision },
        target: { ...request.target },
        ...(request.at ? { at: { ...request.at } } : {}),
        ...(request.to ? { to: { ...request.to } } : {}),
        ...(request.fields
          ? {
              fields: request.fields.map(field => ({
                ...field,
                ...(field.target ? { target: { ...field.target } } : {}),
              })),
            }
          : {}),
      },
      signal,
      controller
    )
      .catch(() => failure('blocked'))
      .finally(() => {
        if (this.running === controller) this.running = null;
      });
    this.requests.set(request.requestId, { identity, result });
    return result;
  }

  private async execute(
    request: WorkspaceOperationRequest,
    signal: AbortSignal | undefined,
    controller: AbortController
  ): Promise<WorkspaceOperationOutcome> {
    if (request.workspaceId !== this.context.workspaceId)
      return failure('blocked');
    // Read-only places stay read-only here too, whatever reached this tab.
    if (request.target.access?.level === 'read') return failure('blocked');
    await Promise.all([...this.starts].map(start => start()));
    const { executeWorkspaceOperation } =
      await import('./workspace-operation-implementation');
    return executeWorkspaceOperation(this.context, request, signal, controller);
  }

  dispose(): void {
    this.disposed = true;
    this.running?.abort();
  }
}
